import { useEffect, useMemo, useState } from "react";
import { Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/utils";

const COLUMNS = [
  { key: "created_at", label: "Date/Time", format: fmtDateTime },
  { key: "actor_name", label: "User" },
  { key: "action", label: "Action" },
  { key: "entity_type", label: "Record Type" },
  { key: "entity_id", label: "Record ID" },
  { key: "details", label: "Details" },
];

// Every field, lowercased and joined — one search box matches anything in
// the row (date, user, action text, record type/id, details), not just one
// column at a time.
const _searchable = (row) => COLUMNS.map((c) => String(row[c.key] ?? "")).join(" ␟ ").toLowerCase();

export default function AuditTrail() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });

  useEffect(() => {
    api.get("/contractor/audit-trail").then((r) => setLogs(r.data)).finally(() => setLoading(false));
  }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let filtered = q ? logs.filter((l) => _searchable(l).includes(q)) : logs;
    const { key, dir } = sort;
    filtered = [...filtered].sort((a, b) => {
      const av = a[key] ?? "";
      const bv = b[key] ?? "";
      const cmp = key === "created_at" ? new Date(av) - new Date(bv) : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });
    return filtered;
  }, [logs, query, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const sortIcon = (key) => {
    if (sort.key !== key) return <ArrowUpDown size={11} className="text-slate-300" />;
    return sort.dir === "asc" ? <ArrowUp size={11} className="text-sky-600" /> : <ArrowDown size={11} className="text-sky-600" />;
  };

  return (
    <div className="p-6 md:p-8" data-testid="audit-trail-page">
      <h1 className="text-2xl font-extrabold tracking-tight">Audit Trail</h1>
      <p className="text-slate-500 text-sm mt-1">Immutable log of every action taken in your workspace.</p>

      <div className="mt-6 relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search every field — user, action, record, details…"
          data-testid="audit-search-input"
          className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-[13px] outline-none focus:border-sky-400"
        />
      </div>

      <div className="mt-4 text-[11.5px] text-slate-400" data-testid="audit-result-count">
        {loading ? "Loading…" : `${rows.length} of ${logs.length} ${logs.length === 1 ? "entry" : "entries"}`}
      </div>

      <div className="overflow-x-auto mt-2">
        <table className="w-full text-[13px]" data-testid="audit-trail-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              {COLUMNS.map((c) => (
                <th key={c.key} className="text-left py-2.5 pr-4">
                  <button
                    onClick={() => toggleSort(c.key)} data-testid={`audit-sort-${c.key}`}
                    className="flex items-center gap-1 hover:text-slate-600"
                  >
                    {c.label} {sortIcon(c.key)}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} className="border-b border-slate-50 align-top" data-testid={`audit-row-${l.id}`}>
                <td className="py-3 pr-4 text-slate-500 whitespace-nowrap">{fmtDateTime(l.created_at)}</td>
                <td className="py-3 pr-4 font-medium">{l.actor_name}</td>
                <td className="py-3 pr-4">{l.action}</td>
                <td className="py-3 pr-4 text-slate-500 capitalize">{l.entity_type ? l.entity_type.replace(/_/g, " ") : "—"}</td>
                <td className="py-3 pr-4 mono text-slate-400">{l.entity_id || "—"}</td>
                <td className="py-3 pr-4 text-slate-600">{l.details || "—"}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={COLUMNS.length} className="text-slate-400 text-sm text-center py-10">
                {logs.length === 0 ? "No activity recorded yet." : "No entries match your search."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
